/* ============================================================
 * src/routes.tsx
 *
 * The v.3 prototype matched routes from a `window.ROUTES` array with
 * its own hash-style matcher. This file restores the same table as a
 * react-router-dom v6 `createBrowserRouter` config so the per-screen
 * paths the wiring tasks expect (e.g. `src/screens/channels/GoLive.tsx`)
 * are the modules React Router actually mounts.
 *
 * Auxiliary surfaces that were stubbed `Pending(...)` in the prototype
 * (e.g. `/library/captions/bulk`, `/automations/:id/logs`) are wired
 * here to the prototype's same stub component drawn from the pending
 * bundle. Downstream wiring tasks will replace those targets one by
 * one when they implement the real surface.
 *
 * The two NEW surfaces the wiring tasks introduce later — LiveTab
 * (T322) and Provisioning (T316) — are not present in the prototype
 * and therefore not registered here. Adding them is the responsibility
 * of those tasks.
 * ============================================================ */
import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Shell } from './layouts/Shell';

import Dashboard from './screens/home/Dashboard';
import LiveMonitor from './screens/monitor/LiveMonitor';
import MonitorDetail from './screens/home/MonitorDetail';

import ChannelsList from './screens/channels/ChannelsList';
import ChannelCreate from './screens/channels/ChannelCreate';
import ChannelDetail from './screens/channels/ChannelDetail';
import GoLive from './screens/channels/GoLive';
import StreamKeys from './screens/channels/StreamKeys';
import EncoderHelper from './screens/channels/EncoderHelper';
import ChannelPerformance from './screens/channels/ChannelPerformance';
import Destinations from './screens/channels/Destinations';
import RecentPublishes from './screens/channels/RecentPublishes';

import Library from './screens/content/Library';
import Upload from './screens/content/Upload';
import ScenesBrowser from './screens/content/ScenesBrowser';
import BulkCaptions from './screens/content/BulkCaptions';
import ContentDetail from './screens/content/ContentDetail';
import MarkerEditor from './screens/content/MarkerEditor';
import Analyse from './screens/content/Analyse';
import ManageCaptions from './screens/content/ManageCaptions';
import EditMetadata from './screens/content/EditMetadata';
import FeedSources from './screens/content/FeedSources';
import FeedHealth from './screens/content/FeedHealth';
import PublishingCapacity from './screens/content/PublishingCapacity';
import SmartSearch from './screens/content/SmartSearch';

import Inbox from './screens/editorial/Inbox';
import EditorialSync from './screens/editorial/EditorialSync';
import EditorialItemReview from './screens/editorial/EditorialItemReview';

import SchedulesList from './screens/schedules/SchedulesList';
import SchedulesEditor from './screens/schedules/SchedulesEditor';
import Templates from './screens/schedules/Templates';
import ProgrammeGuide from './screens/schedules/ProgrammeGuide';

import Analytics from './screens/insights/Analytics';
import ViewerQuality from './screens/insights/ViewerQuality';
import Cost from './screens/insights/Cost';
import Uptime from './screens/insights/Uptime';
import PlayerHealth from './screens/insights/PlayerHealth';
import AdPerformance from './screens/insights/AdPerformance';
import AiSuggestions from './screens/content/AiSuggestions';
import ProviderSpend from './screens/insights/ProviderSpend';

import SystemHealth from './screens/operations/SystemHealth';
import Automations from './screens/operations/Automations';
import AutomationBuilder from './screens/operations/AutomationBuilder';
import AutomationDetail from './screens/operations/AutomationDetail';
import AutomationLogs from './screens/operations/AutomationLogs';
import Alerts from './screens/alerts/Alerts';
import BatchOps from './screens/operations/BatchOps';
import Compliance from './screens/operations/Compliance';
import VersionHistory from './screens/operations/VersionHistory';

import Settings from './screens/settings/Settings';
import DeliveryOps from './screens/delivery/DeliveryOps';
import AdvancedConsole from './screens/settings/AdvancedConsole';
import AdvancedAudit from './screens/settings/AdvancedAudit';

import SignIn from './screens/auth/SignIn';
import SignUp from './screens/auth/SignUp';
import VerifyEmail from './screens/auth/VerifyEmail';
import ForgotPassword from './screens/auth/ForgotPassword';
import Onboarding from './screens/auth/Onboarding';

import ViewerPreview from './screens/states/ViewerPreview';
import FirstRun from './screens/states/FirstRun';
import AccountLocked from './screens/states/AccountLocked';
import SessionExpired from './screens/states/SessionExpired';
import OperatorDashboard from './screens/states/OperatorDashboard';

import { Panel, EmptyState, Btn, PageHead } from './components/primitives';
import { useNavigate } from 'react-router-dom';
import { Bridge } from './lib/route-bridge';

/* ============================================================
 * RouteParamProps (T316, binds FR-021, PRD Appendix E.1)
 *
 * Shared param-prop shape for the `<Bridge component={...} />`
 * lazy-element wrappers below. The v.3 prototype's screens
 * historically destructured route params as React props (e.g.
 * `function GoLive({ id })`) without explicit types, leaving TS
 * to infer `any`. The Bridge wrapper's generic constraint
 * (`P extends Record<string, unknown>`) rejects those `any`
 * inferred shapes, producing 11 TS2322 errors at every `<Bridge
 * component={X} />` call site here.
 *
 * `RouteParamProps` mirrors the shape that `useParams()` returns
 * (each declared URL param is `string | undefined`). The
 * `castParamComponent<T>(c)` helper narrows a screen typed with
 * `any`-shaped props (or no prop types) to a Bridge-compatible
 * `ComponentType<RouteParamProps>` for ROUTING purposes only;
 * the screen's own prop-typing burndown is paid down by T318/T319
 * per Appendix E.1's per-screen plan, NOT here.
 *
 * This is deliberately additive: the helper introduces one cast
 * at the route-table boundary instead of touching every screen
 * signature (which would balloon this task's blast radius past
 * its outputs and into T318/T319 territory).
 * ============================================================ */
export type RouteParamProps = Record<string, string | undefined>;

function castParamComponent<T>(c: T): ComponentType<RouteParamProps> {
  return c as unknown as ComponentType<RouteParamProps>;
}

/* Honest 404 (`<NotFound />` in the prototype). */
function NotFound() {
  const nav = useNavigate();
  return (
    <div style={{ padding: 80 }}>
      <EmptyState
        icon="search"
        title="Página no encontrada"
        body="La ruta que buscas no existe en Circa."
        action={<Btn variant="solid" onClick={() => nav('/')}>Volver al panel</Btn>}
      />
    </div>
  );
}

/* `Pending` placeholder (`function Pending(...)` in app.jsx). */
function Pending({ title, description }: { title: string; description?: string }) {
  return (
    <div>
      <PageHead title={title} description={description} />
      <Panel>
        <EmptyState
          icon="layers"
          title="Aún no disponible"
          body="Esta superficie está definida y se está construyendo. No mostramos datos inventados."
        />
      </Panel>
    </div>
  );
}

export const router = createBrowserRouter([
  /* public — bare (Shell renders <Outlet> without chrome via PUBLIC check) */
  {
    element: <Shell />,
    children: [
      { path: '/sign-in', element: <SignIn /> },
      { path: '/sign-up', element: <SignUp /> },
      { path: '/verify-email', element: <VerifyEmail /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/onboarding', element: <Onboarding /> },
      { path: '/locked', element: <AccountLocked /> },
      { path: '/session-expired', element: <SessionExpired /> },
      { path: '/operator', element: <OperatorDashboard /> },

      /* home */
      { path: '/', element: <Dashboard /> },
      { path: '/monitoring', element: <LiveMonitor /> },
      { path: '/monitoring/:id', element: <Bridge component={castParamComponent(MonitorDetail)} /> },

      /* channels */
      { path: '/channels', element: <ChannelsList /> },
      { path: '/channels/new', element: <ChannelCreate /> },
      { path: '/channels/:id', element: <Bridge component={castParamComponent(ChannelDetail)} /> },
      { path: '/channels/:id/go-live', element: <Bridge component={castParamComponent(GoLive)} /> },
      { path: '/channels/:id/input-keys', element: <Bridge component={castParamComponent(StreamKeys)} /> },
      { path: '/channels/:id/encoder', element: <Bridge component={castParamComponent(EncoderHelper)} /> },
      { path: '/channels/:id/performance', element: <Bridge component={castParamComponent(ChannelPerformance)} /> },
      { path: '/destinations', element: <Destinations /> },
      { path: '/publishes', element: <RecentPublishes /> },

      /* content */
      { path: '/library', element: <Library /> },
      { path: '/library/upload', element: <Upload /> },
      { path: '/library/scenes', element: <ScenesBrowser /> },
      { path: '/library/captions/bulk', element: <BulkCaptions /> },
      { path: '/library/:id', element: <Bridge component={castParamComponent(ContentDetail)} /> },
      { path: '/library/:id/markers', element: <Bridge component={castParamComponent(MarkerEditor)} /> },
      { path: '/library/:id/analyse', element: <Bridge component={castParamComponent(Analyse)} /> },
      { path: '/library/:id/captions', element: <Bridge component={castParamComponent(ManageCaptions)} /> },
      { path: '/library/:id/metadata', element: <Bridge component={castParamComponent(EditMetadata)} /> },
      { path: '/editorial-inbox', element: <Inbox /> },
      { path: '/editorial-inbox/:itemId', element: <Bridge component={castParamComponent(EditorialItemReview)} /> },
      { path: '/editorial-sync', element: <EditorialSync /> },
      { path: '/feed-sources', element: <FeedSources /> },
      { path: '/feed-health', element: <FeedHealth /> },
      { path: '/publishing-capacity', element: <PublishingCapacity /> },
      { path: '/search', element: <SmartSearch /> },

      /* schedules */
      { path: '/schedules', element: <SchedulesList /> },
      { path: '/schedules/:id/edit', element: <Bridge component={castParamComponent(SchedulesEditor)} /> },
      { path: '/templates', element: <Templates /> },
      { path: '/programme-guide', element: <ProgrammeGuide /> },

      /* insights */
      { path: '/analytics', element: <Analytics /> },
      { path: '/quality', element: <ViewerQuality /> },
      { path: '/cost', element: <Cost /> },
      { path: '/uptime', element: <Uptime /> },
      { path: '/player-health', element: <PlayerHealth /> },
      { path: '/ad-performance', element: <AdPerformance /> },
      { path: '/ai-suggestions', element: <AiSuggestions /> },
      { path: '/provider-spend', element: <ProviderSpend /> },

      /* operations */
      { path: '/system-health', element: <SystemHealth /> },
      { path: '/automations', element: <Automations /> },
      { path: '/automations/new', element: <AutomationBuilder /> },
      { path: '/automations/:id', element: <Bridge component={castParamComponent(AutomationDetail)} /> },
      { path: '/automations/:id/logs', element: <Bridge component={castParamComponent(AutomationLogs)} /> },
      { path: '/alerts', element: <Alerts /> },
      { path: '/batch-config', element: <BatchOps /> },
      { path: '/compliance', element: <Compliance /> },
      { path: '/version-history', element: <VersionHistory /> },

      /* settings — the prototype's `Settings` component takes a `sub` prop;
       * with react-router-dom we read the `:sub` URL param via the Bridge.
       * The bare `/settings` path passes no sub (Settings has a default). */
      { path: '/settings', element: <Settings sub="organization" /> },
      { path: '/settings/:sub', element: <Bridge component={castParamComponent(Settings)} /> },
      { path: '/settings/advanced/delivery-ops', element: <DeliveryOps /> },
      /* WIZ-3 / T325 — administrator-only audit-history viewer wired to
       * api.auditQueries.listAuditEvents. The specific route entry must
       * precede the generic `/settings/advanced/:sub` catch-all so
       * react-router-dom picks this screen (not the prototype's
       * AdvancedConsole 'audit' sub-block) for `/settings/advanced/audit`. */
      { path: '/settings/advanced/audit', element: <AdvancedAudit /> },
      { path: '/settings/advanced/:sub', element: <Bridge component={castParamComponent(AdvancedConsole)} /> },

      /* states */
      { path: '/viewer-preview', element: <ViewerPreview /> },
      { path: '/welcome', element: <FirstRun /> },

      /* placeholder for the Pending tribute / unmatched leaves */
      { path: '/pending', element: <Pending title="Aún no disponible" /> },

      /* 404 */
      { path: '*', element: <NotFound /> },
    ],
  },
  /* fallback if any future route slips out of the Shell tree */
  { path: '/index.html', element: <Navigate to="/" replace /> },
]);

export default router;
